import React, { useRef, useState } from 'react';
import { hashKey, useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Printer, X } from 'lucide-react';
import { dashboardWidgetService } from '@services';
import Button from '@components/common/Button';
import { printElement } from '@utils/widgetPrint';
import { showsNoDataNotice } from '@utils/widgetData';
import { showToast } from '@utils/toast';
import type { WidgetSubscription, WidgetSubscriptionSchedule } from '@app-types';
import { DashboardSectionError } from '@components/features/dashboard/DashboardSectionError';
import { SubscriptionFrequencySelect } from './SubscriptionFrequencySelect';
import { WidgetDataView } from './WidgetDataView';
import { WidgetExportMenu } from './WidgetExportMenu';
import { WidgetLoading } from './WidgetLoading';
import styles from './DashboardWidgetCard.module.css';
import './WidgetPrint.module.css';

/**
 * Query key of one widget's data; the Division filter, the chosen dimension and the chosen period are part of it, so
 * changing any of them loads fresh data. Without a dimension and a period the key is the widget's key and Division
 * filter alone, which still matches a prefix that invalidates every dimension and period of the widget.
 */
export const widgetDataKey = (widgetKey: string, divisionIds: number[], dimension?: string, period?: string) => {
  if (period !== undefined) {
    return ['dashboard-widget-data', widgetKey, divisionIds, dimension, period];
  }
  return dimension === undefined
    ? ['dashboard-widget-data', widgetKey, divisionIds]
    : ['dashboard-widget-data', widgetKey, divisionIds, dimension];
};

/** Whether a widget data query was made for the same widget and Division filter, whatever its dimension. */
export const isWidgetDataKeyFor = (queryKey: readonly unknown[], widgetKey: string, divisionIds: number[]): boolean => {
  const prefix = widgetDataKey(widgetKey, divisionIds);
  return queryKey.length >= prefix.length && hashKey(queryKey.slice(0, prefix.length)) === hashKey(prefix);
};

interface DashboardWidgetCardProps {
  widgetKey: string;
  title: string;
  divisionIds: number[];
  subscription: WidgetSubscription | undefined;
  isFirst: boolean;
  isLast: boolean;
  /** A change is being saved; the controls wait for it. */
  busy: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onScheduleChange: (schedule: WidgetSubscriptionSchedule | null) => void;
}

function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 404;
}

/**
 * One Dashboard Widget Placement: its data, the controls to move or remove it, and the choice of how often it is
 * emailed. A widget without a data source yet says so instead of failing.
 */
const DashboardWidgetCard: React.FC<DashboardWidgetCardProps> = ({
  widgetKey,
  title,
  divisionIds,
  subscription,
  isFirst,
  isLast,
  busy,
  onMoveUp,
  onMoveDown,
  onRemove,
  onScheduleChange
}) => {
  const testId = `widget-${widgetKey}`;
  const cardRef = useRef<HTMLElement>(null);
  // Undefined until the user picks one: the widget then opens on its first dimension. Kept for as long as the card is
  // on the Dashboard, and only this card's.
  const [dimension, setDimension] = useState<string | undefined>(undefined);
  // The same for the period, for the widgets that have a period switch of their own.
  const [period, setPeriod] = useState<string | undefined>(undefined);
  const { data, isLoading, isError, error, refetch, isPlaceholderData } = useQuery({
    queryKey: widgetDataKey(widgetKey, divisionIds, dimension, period),
    queryFn: () => dashboardWidgetService.getWidgetData(widgetKey, dimension, period),
    // Switching dimension or period keeps the previous numbers (and the switches themselves) on screen until the new
    // ones arrive; a change of the Division filter does not, as the old numbers would then be for the wrong Divisions.
    placeholderData: (previous, previousQuery) =>
      previousQuery && isWidgetDataKeyFor(previousQuery.queryKey, widgetKey, divisionIds) ? previous : undefined,
    // A 404 is the widget saying it has no data yet; retrying would only delay that message.
    retry: false,
    refetchOnMount: 'always'
  });

  const print = () => {
    if (!cardRef.current) {
      return;
    }
    try {
      printElement(cardRef.current);
    } catch {
      showToast.error('This widget could not be printed.');
    }
  };

  const renderBody = () => {
    if (isError && isNotFound(error)) {
      return (
        <p className={styles.message} data-test-id={`${testId}-no-data`}>
          This widget has no data yet.
        </p>
      );
    }
    if (isError) {
      return (
        <DashboardSectionError
          message={`Failed to load ${title}.`}
          onRetry={() => refetch()}
          dataTestId={`${testId}-error`}
        />
      );
    }
    if (isLoading || !data) {
      return <WidgetLoading dataTestId={`${testId}-loading`} />;
    }

    return (
      <WidgetDataView
        data={data}
        testIdPrefix={testId}
        onDimensionChange={setDimension}
        onPeriodChange={setPeriod}
        showNoDataNotice={showsNoDataNotice(widgetKey)}
      />
    );
  };

  return (
    <section className={styles.card} data-test-id={testId} aria-label={title} ref={cardRef}>
      <header className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        {/* The controls are for working with the Dashboard; a printout of the widget leaves them out. */}
        <div className={`${styles.controls} ${styles.noPrint}`} data-test-id={`${testId}-controls`}>
          <Button
            variant="ghost"
            size="xs"
            onClick={print}
            disabled={!data || isPlaceholderData}
            aria-label={`Print ${title}`}
            dataTestId={`${testId}-print`}
          >
            <Printer size={16} />
          </Button>
          <WidgetExportMenu
            title={title}
            data={isPlaceholderData ? undefined : data}
            getChartSvg={() => cardRef.current?.querySelector<SVGSVGElement>('svg.recharts-surface') ?? null}
            testIdPrefix={testId}
          />
          <Button
            variant="ghost"
            size="xs"
            onClick={onMoveUp}
            disabled={busy || isFirst}
            aria-label={`Move ${title} up`}
            dataTestId={`${testId}-move-up`}
          >
            <ArrowUp size={16} />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={onMoveDown}
            disabled={busy || isLast}
            aria-label={`Move ${title} down`}
            dataTestId={`${testId}-move-down`}
          >
            <ArrowDown size={16} />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={onRemove}
            disabled={busy}
            aria-label={`Remove ${title} from the Dashboard`}
            dataTestId={`${testId}-remove`}
          >
            <X size={16} />
          </Button>
        </div>
      </header>

      <div className={`${styles.subscription} ${styles.noPrint}`} data-test-id={`${testId}-subscription-row`}>
        <SubscriptionFrequencySelect
          widgetKey={widgetKey}
          subscription={subscription}
          disabled={busy}
          onChange={onScheduleChange}
        />
      </div>

      {renderBody()}
    </section>
  );
};

export { DashboardWidgetCard };
